/* import __COLOCATED_TEMPLATE__ from './view-mode-options.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { all } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import type ReportingStandalone from 'embercom/services/reporting-standalone';
import { type TaskGenerator } from 'ember-concurrency';
import type Report from 'embercom/models/reporting/custom/report';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { flatten } from 'underscore';
import { type RawChartData } from 'embercom/services/reporting-chart-service';
import { hasFeature } from 'embercom/helpers/has-feature';

interface Args {
  report: Report;
  duplicateReport: () => TaskGenerator<void>;
  deleteReport: () => TaskGenerator<void>;
  onClickEditReport: () => void;
  editReportDisabled: boolean;
  isStandalone: boolean;
  openShareSideDrawer: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const SNAPSHOT_URL = '/ember/reporting/custom_reports/snapshot';
const SIGNAL_FLEXIBLE_URL = '/ember/reporting/signal_flexible';
const SNAPSHOT_STORAGE_KEY = 'reporting_snapshot_report';

export default class ReportingCustomReportViewModeOptions extends Component<Signature> {
  @service declare reportingStandalone: ReportingStandalone;
  @service declare reportingChartService: ReportingChartService;
  @service declare appService: any;

  get app() {
    return this.appService.app;
  }

  get shouldShowReportAction() {
    if (this.args.isStandalone) {
      return this.reportingStandalone.supportsFeature('showReportAction');
    }
    return true;
  }

  get shouldShowShareOption() {
    if (this.args.isStandalone) {
      return this.reportingStandalone.supportsFeature('shareReport');
    }
    return true;
  }

  get shouldShowSnapshotBtn() {
    return (
      hasFeature('reporting-external-sharing', this.appService) &&
      localStorage.getItem(SNAPSHOT_STORAGE_KEY) === 'true'
    );
  }

  async fetchData(dataRequest: $TSFixMe) {
    return await ajax({
      url: SIGNAL_FLEXIBLE_URL,
      type: 'POST',
      data: JSON.stringify({
        app_id: this.appService.app.id,
        admin_id: this.appService.app.currentAdmin.id,
        ...dataRequest,
        ...(this.reportingChartService.useSyntheticData
          ? { use_synthetic_data: this.reportingChartService.useSyntheticData }
          : {}),
      }),
    });
  }

  @task({ drop: true })
  *snapshotReport(): TaskGenerator<void> {
    let requestSnapshots = this.args.report.snapshotFlexibleRequest();

    let responsePromises = requestSnapshots.map(async (snapshot: any) => {
      let id = snapshot.chart_id;
      let dataRequests = snapshot.requests;
      let responses = await all(
        dataRequests.map((dataRequest: any) => this.fetchData(dataRequest)),
      );

      return {
        id,
        responses: responses.map((response: any) => response),
      };
    });
    let rawResponses = flatten(yield all(responsePromises));

    this.postSnapshot(rawResponses);
  }

  private async postSnapshot(rawData: Array<RawChartData>) {
    return await ajax({
      url: SNAPSHOT_URL,
      type: 'POST',
      data: JSON.stringify({
        app_id: this.app.id,
        admin_id: this.app.currentAdmin.id,
        report_id: this.args.report.id,
        raw_data: rawData,
        use_synthetic_data: this.reportingChartService.useSyntheticData,
      }),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::ViewModeOptions': typeof ReportingCustomReportViewModeOptions;
    'reporting/custom/report/view-mode-options': typeof ReportingCustomReportViewModeOptions;
  }
}
